import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties, useState } from 'react';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { getSortedAppearancesFromRules } from '@sorare/core/src/lib/players';

import { PlayerCard } from '@sorare/gameplay/src/components/lineup/PlayerCard';
import PlayerCardPlaceholder from '@sorare/gameplay/src/components/lineup/PlayerCardPlaceholder';
import { So5AppearanceDialog } from '@sorare/gameplay/src/components/stats/So5AppearanceDialog';
import { isFixtureOpened } from '@sorare/gameplay/src/lib/so5';

import { BaseballSo5Lineup_so5LeaderboardContender } from './__generated__/index.graphql';

export const Root = styled(Horizontal)`
  --gap: var(--unit);
  --item-width: calc(
    (100% - (var(--nb-items-max-per-row) - 1) * var(--gap)) /
      var(--nb-items-max-per-row)
  );

  max-width: var(--lineup-max-width);
  margin: auto;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--gap);

  > * {
    flex-basis: var(--item-width);
  }

  .transitioning + &,
  .standalone & {
    view-transition-name: lineup-field-cards;
    > *:nth-child(1) {
      view-transition-name: lineup-field-card-1;
    }
    > *:nth-child(2) {
      view-transition-name: lineup-field-card-2;
    }
    > *:nth-child(3) {
      view-transition-name: lineup-field-card-3;
    }
    > *:nth-child(4) {
      view-transition-name: lineup-field-card-4;
    }
    > *:nth-child(5) {
      view-transition-name: lineup-field-card-5;
    }
    > *:nth-child(6) {
      view-transition-name: lineup-field-card-6;
    }
    > *:nth-child(7) {
      view-transition-name: lineup-field-card-7;
    }
  }

  &.smartWidth {
    --card-footer-height: 52px;
    --usable-height: calc(var(--100vh) * var(--height-percentage));
    --card-height: calc(
      (var(--usable-height) - var(--gap) - 2 * var(--card-footer-height)) / 2
    );
    --calc-card-width: calc(var(--card-height) * var(--card-aspect-ratio));
    --width: calc(
      var(--nb-items-max-per-row) * var(--calc-card-width) +
        (var(--nb-items-max-per-row) - 1) * var(--gap)
    );
    max-width: unset;
    width: clamp(300px, var(--width), 100%);
  }
`;
export const SlotFooter = styled.div`
  display: grid;
  grid-template-rows: var(--triple-unit) var(--double-unit);
  gap: var(--half-unit);
  justify-items: center;
`;

type Props = {
  so5LeaderboardContender: BaseballSo5Lineup_so5LeaderboardContender;
  variant: 'oneRow' | 'twoRows';
  className?: string;
  fillHeightPercent?: number;
};

export const So5Lineup = ({
  so5LeaderboardContender,
  variant = 'oneRow',
  fillHeightPercent,
  className,
}: Props) => {
  const [so5AppearanceId, setSo5AppearanceId] = useState<string>();

  const { so5Leaderboard, so5Lineup, managerTeam } = so5LeaderboardContender;

  const fixtureOpened = isFixtureOpened(so5Leaderboard.so5Fixture);

  const appearances = getSortedAppearancesFromRules({
    so5Lineup: so5Lineup || undefined,
    so5Leaderboard,
  });

  return (
    <>
      <Root
        className={classNames(className, { smartWidth: !!fillHeightPercent })}
        style={
          {
            '--height-percentage': fillHeightPercent,
            '--nb-items-max-per-row': variant === 'oneRow' ? 7 : 4,
            '--lineup-max-width': variant === 'oneRow' ? '100%' : '600px',
          } as CSSProperties
        }
      >
        {appearances.map((appearance, i) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i}>
              {'id' in appearance ? (
                <PlayerCard
                  appearance={appearance}
                  showScore
                  withDate={false}
                  withInlineGamesDots
                  onClick={() => {
                    setSo5AppearanceId(appearance.id);
                  }}
                  Footer={SlotFooter}
                />
              ) : (
                <PlayerCardPlaceholder
                  position={i}
                  lineup={so5Lineup}
                  managerTeam={managerTeam}
                  leaderboard={so5Leaderboard}
                  defaultRarity={so5Leaderboard.mainRarityType || undefined}
                  fixtureOpened={fixtureOpened}
                  Footer={SlotFooter}
                />
              )}
            </div>
          );
        })}
      </Root>
      {so5AppearanceId && (
        <So5AppearanceDialog
          open
          onClose={() => setSo5AppearanceId(undefined)}
          showUpsell
          so5AppearanceId={so5AppearanceId}
        />
      )}
    </>
  );
};

So5Lineup.fragments = {
  so5LeaderboardContender: gql`
    fragment BaseballSo5Lineup_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        so5Appearances {
          id
          position
          so5Score {
            id
          }
          ...PlayerCard_so5Appearance
        }
        ...PlayerCardPlaceholder_so5Lineup
        ...getSortedAppearancesFromRules_so5Lineup
      }
      managerTeam {
        id
        ...PlayerCardPlaceholder_managerTeam
      }
      so5Leaderboard {
        slug
        mainRarityType
        so5Fixture {
          slug
          aasmState
        }
        rules {
          id
          appearances {
            name
          }
        }
        ...PlayerCardPlaceholder_so5Leaderboard
        ...getSortedAppearancesFromRules_so5Leaderboard
      }
    }
    ${PlayerCard.fragments.so5Appearance}
    ${PlayerCardPlaceholder.fragments.so5Lineup}
    ${PlayerCardPlaceholder.fragments.managerTeam}
    ${PlayerCardPlaceholder.fragments.so5Leaderboard}
    ${getSortedAppearancesFromRules.fragments.so5Lineup}
    ${getSortedAppearancesFromRules.fragments.so5Leaderboard}
  ` as TypedDocumentNode<BaseballSo5Lineup_so5LeaderboardContender>,
};
